import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileSpreadsheet, Loader2, ArrowRight, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * QuoteImportDialog — drop an xlsx/csv SOQ, pick the sheet, map the
 * columns, tick which rows to import, hit Create. Backend does the
 * math + auto-detection; this dialog is just the UI shell.
 *
 * Trigger via <QuoteImportDialog open onClose ... /> — passing a File
 * up-front (from a top-level drop-zone) skips step 1 and goes straight
 * to sheet selection.
 */
export function QuoteImportDialog({ open, onClose, onCreated, initialFile }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [sheetName, setSheetName] = useState(null);
  const [mapping, setMapping] = useState({});
  const [rowSel, setRowSel] = useState(new Set());
  const [meta, setMeta] = useState({ client_name: "", client_email: "", client_address: "", valid_until: "", notes: "", gst_inclusive: false });
  const [step, setStep] = useState(1); // 1: drop, 2: sheet, 3: map+rows, 4: meta+create
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null); setParsed(null); setSheetName(null); setMapping({});
    setRowSel(new Set()); setStep(1); setCreating(false);
    setMeta({ client_name: "", client_email: "", client_address: "", valid_until: "", notes: "", gst_inclusive: false });
    if (initialFile) {
      // Trigger the upload right away
      handleUpload(initialFile);
    }
  }, [open, initialFile]);

  const handleUpload = async (f) => {
    if (!f) return;
    setFile(f); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/quotes/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      if ((data.sheets || []).length === 1) {
        chooseSheet(data.sheets[0], data);
      } else {
        setStep(2);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't parse the file");
    } finally {
      setUploading(false);
    }
  };

  const chooseSheet = (sheet, data = parsed) => {
    setSheetName(sheet.name);
    setMapping(sheet.auto_mapping || {});
    // Pre-select every VALID row by default so the office ticks OUT
    // what they don't want. We do the same trailing-total + section
    // detection as the preview so the initial count matches.
    const TOTAL_TOKENS = ["total", "sub-total", "subtotal", "gst", "nzd",
                          "grand total", "total excluding gst",
                          "total inc gst", "total including gst"];
    const isTrailingTotal = (d) => {
      const s = String(d || "").trim().toLowerCase();
      return !!s && TOTAL_TOKENS.some((t) => s === t || s.startsWith(t));
    };
    const m = sheet.auto_mapping || {};
    const selected = new Set();
    (sheet.sample_rows || []).forEach((cells, i) => {
      const desc = m.description != null ? cells[m.description] : "";
      const descStr = String(desc || "").trim();
      if (descStr && !isTrailingTotal(descStr)) selected.add(i);
    });
    setRowSel(selected);
    setStep(3);
  };

  const activeSheet = useMemo(
    () => (parsed?.sheets || []).find((s) => s.name === sheetName),
    [parsed, sheetName],
  );

  const toggleRow = (i) => {
    setRowSel((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };
  const toggleAll = () => {
    if (!activeSheet) return;
    const validIdxs = previewRows.filter((r) => r.valid).map((r) => r.idx);
    setRowSel((s) => {
      const allSelected = validIdxs.every((i) => s.has(i));
      if (allSelected) return new Set();
      return new Set(validIdxs);
    });
  };

  const previewRows = useMemo(() => {
    if (!activeSheet) return [];
    // Trailing-totals patterns we grey out in the preview (backend
    // skips them at create time)
    const TOTAL_TOKENS = ["total", "sub-total", "subtotal", "gst", "nzd",
                          "grand total", "total excluding gst",
                          "total inc gst", "total including gst"];
    const isTrailingTotal = (d) => {
      const s = String(d || "").trim().toLowerCase();
      if (!s) return false;
      return TOTAL_TOKENS.some((t) => s === t || s.startsWith(t));
    };
    const looksLikeSectionItemNo = (v) => {
      const s = String(v || "").trim().replace(/\s+/g, "");
      return /^\d+\.0*$/.test(s);
    };
    return activeSheet.sample_rows.map((cells, idx) => {
      const item = mapping.item != null ? cells[mapping.item] : "";
      const desc = mapping.description != null ? cells[mapping.description] : "";
      const qty = mapping.qty != null ? cells[mapping.qty] : "";
      const rate = mapping.unit_price != null ? cells[mapping.unit_price] : "";
      const total = mapping.total != null ? cells[mapping.total] : "";
      const descStr = String(desc || "").trim();
      const hasNoNumbers = String(qty || "").trim() === "" &&
                            String(rate || "").trim() === "" &&
                            String(total || "").trim() === "";
      const isSection = descStr !== "" && (looksLikeSectionItemNo(item) || hasNoNumbers);
      const isTotal = isTrailingTotal(descStr);
      const validRow = descStr !== "" && !isTotal;
      return { idx, cells, item, desc, qty, rate, total, isSection, isTotal, valid: validRow };
    });
  }, [activeSheet, mapping]);

  const importableCount = previewRows.filter((r) => r.valid && rowSel.has(r.idx)).length;

  const create = async () => {
    if (!meta.client_name.trim()) { toast.error("Customer name is required"); return; }
    setCreating(true);
    try {
      const rows = previewRows
        .filter((r) => rowSel.has(r.idx) && r.valid)
        .map((r) => ({ cells: r.cells, is_section: r.isSection }));
      const { data } = await api.post("/quotes/import/create", {
        ...meta,
        rows,
        mapping,
      });
      toast.success(`Quote ${data.quote_number} created with ${rows.length} rows`);
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="quote-import-dialog">
        <DialogHeader>
          <DialogTitle>Import quote from spreadsheet</DialogTitle>
          <DialogDescription>
            Drop an Excel (.xlsx) or CSV Schedule of Quantities. We&apos;ll auto-match your Description / Qty / Unit / Rate columns — you can tweak any of them below before importing.
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 — DROP */}
        {step === 1 && (
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[#FF5F15]"); }}
            onDragLeave={(e) => e.currentTarget.classList.remove("border-[#FF5F15]")}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("border-[#FF5F15]");
              const f = e.dataTransfer.files?.[0];
              if (f) handleUpload(f);
            }}
            className="border-2 border-dashed border-zinc-300 dark:border-zinc-700 rounded-sm p-10 text-center hover:border-[#FF5F15] transition-colors cursor-pointer"
            onClick={() => inputRef.current?.click()}
            data-testid="quote-import-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="quote-import-input"
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-[#FF5F15] animate-spin" />
                <div className="text-[13px] font-semibold">Reading {file?.name || "file"}…</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <UploadCloud className="w-10 h-10 text-zinc-400" />
                <div className="font-display font-black uppercase tracking-tight text-lg">Drop your SOQ here</div>
                <div className="text-[12px] text-zinc-500">or tap to browse · .xlsx / .csv up to 8 MB</div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — SHEET PICKER */}
        {step === 2 && parsed && (
          <div>
            <div className="text-[12px] text-zinc-600 dark:text-zinc-400 mb-3">
              Your file has {parsed.sheets.length} sheets — pick the one with the priced schedule.
            </div>
            <div className="space-y-2" data-testid="quote-import-sheet-picker">
              {parsed.sheets.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => chooseSheet(s)}
                  className="w-full text-left p-3 rounded-sm border-2 border-zinc-200 dark:border-zinc-800 hover:border-[#FF5F15] transition-colors flex items-center gap-3"
                  data-testid={`quote-import-sheet-${s.name}`}
                >
                  <FileSpreadsheet className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1">
                    <div className="font-semibold text-[14px]">{s.name}</div>
                    <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest">{s.row_count} rows · {s.headers.length} columns</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-400" />
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="mt-3 rounded-sm">
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
            </Button>
          </div>
        )}

        {/* STEP 3 — MAPPING + ROW SELECTION */}
        {step === 3 && activeSheet && (
          <div>
            {parsed?.sheets && (() => {
              const sh = (parsed.sheets || []).find((s) => s.name === sheetName);
              const skipped = sh?.header_row_idx || 0;
              if (skipped > 0) {
                return (
                  <div className="mb-3 p-2 border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 rounded-sm text-[11px] text-blue-800 dark:text-blue-200" data-testid="quote-import-header-hint">
                    Detected the real header row on <b>row {skipped + 1}</b> — skipped {skipped} banner/metadata row{skipped === 1 ? "" : "s"} at the top.
                  </div>
                );
              }
              return null;
            })()}

            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              {["item", "description", "qty", "unit", "unit_price", "total"].map((k) => (
                <div key={k}>
                  <Label className="text-[10px] font-bold uppercase tracking-widest">
                    {k === "unit_price" ? "Rate" : k === "item" ? "Item #" : k}
                    {k === "description" && <span className="text-red-500"> *</span>}
                  </Label>
                  <Select
                    value={mapping[k] == null ? "__none__" : String(mapping[k])}
                    onValueChange={(v) => setMapping((m) => ({ ...m, [k]: v === "__none__" ? null : parseInt(v, 10) }))}
                  >
                    <SelectTrigger className="rounded-sm text-[12px]" data-testid={`quote-import-map-${k}`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— none —</SelectItem>
                      {activeSheet.headers.map((h, i) => (
                        <SelectItem key={i} value={String(i)}>{h || `Col ${i + 1}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between mb-2">
              <div className="text-[12px] font-semibold">
                <span className="text-[#FF5F15]">{importableCount}</span> of {previewRows.filter((r) => r.valid).length} rows selected
              </div>
              <Button variant="outline" size="sm" onClick={toggleAll} className="rounded-sm text-[11px] h-7" data-testid="quote-import-toggle-all">
                {rowSel.size === activeSheet.sample_rows.length ? "Deselect all" : "Select all"}
              </Button>
            </div>

            <div className="border border-zinc-200 dark:border-zinc-800 rounded-sm max-h-[38dvh] overflow-y-auto">
              <table className="w-full text-[12px]" data-testid="quote-import-preview-table">
                <thead className="sticky top-0 bg-zinc-100 dark:bg-[#151C27] border-b border-zinc-200 dark:border-zinc-800">
                  <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2 w-14">Item</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="px-2 py-2 text-right">Qty</th>
                    <th className="px-2 py-2 text-right">Unit</th>
                    <th className="px-2 py-2 text-right">Rate</th>
                    <th className="px-2 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {previewRows.map((r) => (
                    <tr
                      key={r.idx}
                      className={`${r.isSection ? "bg-orange-50 dark:bg-orange-900/20 font-bold" : ""} ${r.isTotal ? "bg-zinc-100 dark:bg-zinc-800 line-through text-zinc-400" : ""} ${!r.valid && !r.isTotal ? "opacity-40" : ""}`}
                      data-testid={`quote-import-row-${r.idx}`}
                      title={r.isTotal ? "Trailing totals row — will be skipped" : (r.isSection ? "Section header" : "")}
                    >
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={rowSel.has(r.idx)}
                          onCheckedChange={() => toggleRow(r.idx)}
                          disabled={!r.valid}
                          data-testid={`quote-import-row-check-${r.idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[11px] text-zinc-500">{String(r.item || "").trim() || <span className="text-zinc-300">—</span>}</td>
                      <td className="px-2 py-1.5 max-w-md truncate">{r.desc || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{r.qty}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-[11px] text-zinc-500">{String(r.cells?.[mapping.unit] || "").trim()}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{r.rate}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <Button variant="ghost" size="sm" onClick={() => setStep(parsed?.sheets?.length > 1 ? 2 : 1)} className="rounded-sm">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
              </Button>
              <Button
                onClick={() => setStep(4)}
                disabled={importableCount === 0 || mapping.description == null}
                className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-white font-bold"
                data-testid="quote-import-next"
              >
                Next: customer details <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4 — CUSTOMER + CREATE */}
        {step === 4 && (
          <div className="space-y-3">
            <div>
              <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Customer name <span className="text-red-500">*</span></Label>
              <Input value={meta.client_name} onChange={(e) => setMeta({ ...meta, client_name: e.target.value })} className="rounded-sm mt-1" data-testid="quote-import-client-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Email</Label>
                <Input type="email" value={meta.client_email} onChange={(e) => setMeta({ ...meta, client_email: e.target.value })} className="rounded-sm mt-1" data-testid="quote-import-client-email" />
              </div>
              <div>
                <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Valid until</Label>
                <Input type="date" value={meta.valid_until} onChange={(e) => setMeta({ ...meta, valid_until: e.target.value })} className="rounded-sm mt-1" data-testid="quote-import-valid-until" />
              </div>
            </div>
            <div>
              <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Site address</Label>
              <Input value={meta.client_address} onChange={(e) => setMeta({ ...meta, client_address: e.target.value })} className="rounded-sm mt-1" data-testid="quote-import-client-address" />
            </div>
            <div>
              <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Notes / cover letter</Label>
              <Textarea value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} className="rounded-sm mt-1 min-h-20" data-testid="quote-import-notes" />
            </div>
            <label className="flex items-center gap-2 p-2 border border-zinc-200 dark:border-zinc-800 rounded-sm cursor-pointer" data-testid="quote-import-gst-toggle">
              <Checkbox checked={meta.gst_inclusive} onCheckedChange={(v) => setMeta({ ...meta, gst_inclusive: !!v })} />
              <span className="text-[13px] font-semibold">Prices include GST</span>
              <span className="text-[11px] text-zinc-500 ml-auto">(Default is GST exclusive for NZ SOQs)</span>
            </label>
            <div className="flex items-center justify-between mt-3">
              <Button variant="ghost" size="sm" onClick={() => setStep(3)} className="rounded-sm">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
              </Button>
              <Button
                onClick={create}
                disabled={creating || !meta.client_name.trim()}
                className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-white font-bold"
                data-testid="quote-import-create"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1.5" />}
                Create draft quote — {importableCount} rows
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
